/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';

import {
  DAY_KIND_OFF,
  DAY_KIND_ALL,
  DAY_KIND_RANGE,
  DEFAULT_START,
  DEFAULT_STOP,
} from '../../../helpers/schedule-tag-format';
import { DAY_LABELS, SHORT_DAY_LABELS } from '../../../helpers/schedule-display';

/**
 * One day of a Custom_Schedule: off, all day, or a start and stop time (Requirement 5.3).
 *
 * Presentational only: the day's current choice arrives as a prop and every change is reported
 * through `onChange` as the complete next `{ kind, start, stop }`. The Schedule_Modal owns the
 * observable state, so this component holds none and does no data fetching. Reporting the whole
 * next value rather than a delta keeps the parent free of any knowledge of how a kind change and a
 * time change interact.
 *
 * The times offered come entirely from the `timeOptions` prop, which the modal derives from
 * `timeOptions(schedulerIntervalMinutes)`. This component never builds a time list of its own
 * (Requirement 5.4): the scheduler only wakes on its invocation interval, so offering a finer grain
 * would promise a precision it cannot deliver, and the interval is a deployment setting that only
 * arrives in the SchedulingInfo response.
 *
 * Props:
 * - day: one of DAY_ORDER, e.g. 'MON'
 * - value: { kind: 'off' | 'all' | 'range', start: 'HH:MM', stop: 'HH:MM' }
 * - timeOptions: [{ key, value, text }] from `schedule-display.js`
 * - onChange: (nextValue) => void
 * - disabled: boolean
 */

// The kind labels are written here rather than derived, because they are this control's wording for
// the three states, not a property of the grammar. The values themselves come from the grammar
// helper so that the row, the modal and the tag builder cannot drift apart.
const KIND_OPTIONS = [
  { value: DAY_KIND_OFF, text: 'Off' },
  { value: DAY_KIND_ALL, text: 'All day' },
  { value: DAY_KIND_RANGE, text: 'Start and stop' },
];

const PLACEHOLDER_TEXT = {
  [DAY_KIND_OFF]: 'Not scheduled',
  [DAY_KIND_ALL]: 'Runs all day',
};

/**
 * The option list for one time control.
 *
 * The supplied options are rendered as given. When the value currently set is not among them the
 * value is added as a single extra option, labelled, so that a schedule written outside Service
 * Workbench at a time the interval does not land on is shown as it actually is rather than as a
 * blank control or, worse, as some other time the user did not choose. That is the opposite of
 * generating a time list: nothing is offered that is not either supplied or already set.
 */
function optionsIncludingValue(timeOptions, value) {
  const options = timeOptions || [];
  if (typeof value !== 'string' || value.length === 0) return options;
  if (options.some(option => option.value === value)) return options;

  return [{ key: value, value, text: `${value} (currently set)` }, ...options];
}

class WorkspaceScheduleDayRow extends React.Component {
  get value() {
    const value = this.props.value || {};

    return {
      kind: value.kind || DAY_KIND_OFF,
      // A day that has never been a range still needs times to show once it becomes one, and they
      // have to be the same defaults the grammar helper uses or a round trip would not be stable.
      start: value.start || DEFAULT_START,
      stop: value.stop || DEFAULT_STOP,
    };
  }

  emit = next => {
    if (this.props.disabled) return;

    const onChange = this.props.onChange;
    if (onChange) onChange(next);
  };

  handleKindChange = event => {
    const kind = event.target.value;
    const value = this.value;
    if (kind === value.kind) return;

    // The times are carried through a kind change rather than reset, so that switching a day off and
    // back on again does not lose the hours the user chose.
    this.emit({ ...value, kind });
  };

  handleStartChange = event => {
    this.emit({ ...this.value, start: event.target.value });
  };

  handleStopChange = event => {
    this.emit({ ...this.value, stop: event.target.value });
  };

  renderTimeSelect(kindOfTime, selected, onChange) {
    const day = this.props.day;
    const isStart = kindOfTime === 'start';

    return (
      <select
        className="wsch-modal-select"
        value={selected}
        disabled={!!this.props.disabled}
        onChange={onChange}
        aria-label={`${DAY_LABELS[day] || day} ${isStart ? 'start' : 'stop'} time`}
        data-testid={`schedule-day-${day}-${kindOfTime}`}
      >
        {optionsIncludingValue(this.props.timeOptions, selected).map(option => (
          <option key={option.key || option.value} value={option.value}>
            {option.text}
          </option>
        ))}
      </select>
    );
  }

  renderTimes() {
    const value = this.value;

    if (value.kind !== DAY_KIND_RANGE) {
      return (
        <div className="wsch-modal-day-times-placeholder" data-testid={`schedule-day-${this.props.day}-placeholder`}>
          {PLACEHOLDER_TEXT[value.kind] || PLACEHOLDER_TEXT[DAY_KIND_OFF]}
        </div>
      );
    }

    return (
      <div className="wsch-modal-day-times">
        {this.renderTimeSelect('start', value.start, this.handleStartChange)}
        <span className="wsch-modal-day-time-sep">to</span>
        {this.renderTimeSelect('stop', value.stop, this.handleStopChange)}
      </div>
    );
  }

  render() {
    const day = this.props.day;
    const value = this.value;
    const disabled = !!this.props.disabled;

    return (
      <div className="wsch-modal-day-row" data-testid={`schedule-day-${day}`}>
        <span className="wsch-modal-day-label">{SHORT_DAY_LABELS[day] || day}</span>
        <select
          className="wsch-modal-select wsch-modal-day-kind"
          value={value.kind}
          disabled={disabled}
          onChange={this.handleKindChange}
          aria-label={`${DAY_LABELS[day] || day} timetable`}
          data-testid={`schedule-day-${day}-kind`}
        >
          {KIND_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
        {this.renderTimes()}
      </div>
    );
  }
}

export default WorkspaceScheduleDayRow;
export { KIND_OPTIONS };
